/**
 * TimelineControls Component
 *
 * Bottom control panel with zoom, grouping, and sorting controls.
 * Positioned at bottom center of timeline container.
 */

import React from 'react'
import { Stack, useMediaQuery, useTheme } from '@mui/material'
import { SELECTOR_BOTTOM_PADDING } from '../timeline-constants'
import { TimelineViewToggle } from './controls/timeline-view-toggle'
import { TimelineZoomSlider } from './controls/timeline-zoom-slider'
import { TimelineGroupToggle } from './controls/timeline-group-toggle'
import { TimelineSortToggle } from './controls/timeline-sort-toggle'
import { TimelineCollapseToggle } from './controls/timeline-collapse-toggle'
import { TimelineSuccessionToggle } from './controls/timeline-succession-toggle'
import { TimelineOrgTaskToggle } from './controls/timeline-org-task-toggle'
import { TpToggleButtonGroup } from '~/components'
import { CONTROLS_BREAKPOINT } from './controls/timeline-controls-constants'

/**
 * TimelineControls - Bottom control panel
 *
 * Features:
 * - Collapse all button (only shown when crops are expanded)
 * - View toggle (phases/tasks/both)
 * - Zoom slider (adjust pixels per week)
 * - Grouping toggle (none/species/location/family)
 * - Sorting toggle (alphabetical/chronological)
 *
 * @param {Object} props
 * @param {boolean} props.hideToggleButtons - Hide entire controls panel
 */
export const TimelineControls = ({ hideToggleButtons }) => {
  const theme = useTheme()
  const isExpanded = useMediaQuery(theme.breakpoints.up(CONTROLS_BREAKPOINT))

  if (hideToggleButtons) return null

  return (
    <Stack
      justifySelf='center'
      sx={{
        zIndex: 30,
        position: 'absolute',
        bottom: SELECTOR_BOTTOM_PADDING,
      }}
    >
      {isExpanded ? (
        <Stack spacing={{ lg: 1.5, xl: 3 }} direction='row'>
          <TimelineControlItems />
        </Stack>
      ) : (
        <TpToggleButtonGroup
          sx={{
            '& .MuiToggleButton-root': {
              px: { xs: '1rem', md: '1rem' },
            }
          }}
        >
          <TimelineControlItems />
        </TpToggleButtonGroup>
      )}
    </Stack>
  )
}

const TimelineControlItems = () => {
  return (
    <>
      {/* Collapse all button */}
      <TimelineCollapseToggle />

      {/* Org tasks toggle */}
      <TimelineOrgTaskToggle />

      {/* Succession toggle */}
      <TimelineSuccessionToggle />

      {/* View toggle */}
      <TimelineViewToggle />
      
      {/* Zoom slider */}
      <TimelineZoomSlider />

      {/* Grouping toggle */}
      <TimelineGroupToggle />

      {/* Sorting toggle */}
      <TimelineSortToggle />
    </>
  )
}